import { getTheme } from '@/utils/theme'
import {
  Cascader,
  DatePicker,
  DatePickerRange,
  Divider,
  Input,
  InputNumber,
} from '@seiue/ui'
import { isArray } from '@seiue/util'
import { Upload } from 'antd'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { FieldTypeEnum, QuestionnaireReqColumn } from 'packages/sdks-next/growp'
import React, { useContext } from 'react'
import styled from 'styled-components'

import { OptionList } from './OptionList'
import { OptionSetting } from './OptionSetting'
import { QuestionnaireContext } from './context'
import {
  Option,
  ReqColumnWithMetaData,
  ReqWithMetaData,
  SelectTypeEnum,
} from './types'
import { changeQuestionnaireData } from './utils'
const theme = getTheme()

/**
 * 题目表单项组件
 *
 * @param param - 参数
 * @param param.questionnaire - 当前题目结构
 * @param param.currentIndex - 当前题目索引|包含描述和分页
 * @returns React.ReactNode
 */
export const SelectComponent: React.FC<{
  questionnaire: ReqWithMetaData
  currentIndex: number
}> = ({ questionnaire, currentIndex }) => {
  const { questionnaires, setQuestionnaires } = useContext(QuestionnaireContext)

  const onChangeData = (params: object) => {
    const newQuestionnaires = changeQuestionnaireData(
      questionnaires,
      currentIndex,
      { ...questionnaire, ...params }
    )
    setQuestionnaires([...newQuestionnaires])
  }

  const getComponent = (
    questionnaire: ReqWithMetaData | ReqColumnWithMetaData,
    index: number,
    isProductChild?: boolean
  ) => {
    const { type } = questionnaire || {}
    switch (type) {
      case FieldTypeEnum.String: {
        return (
          <Input
            disabled={true}
            placeholder="请输入"
          />
        )
      }
      case FieldTypeEnum.Text: {
        return (
          <Input.TextArea
            disabled={true}
            placeholder="请输入文本"
            style={{
              minHeight: '82px',
              maxHeight: '82px',
            }}
          />
        )
      }
      case FieldTypeEnum.Integer: {
        return (
          <InputNumber
            style={{
              width: '680px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
            disabled={true}
            placeholder="请输入数字"
          />
        )
      }
      case FieldTypeEnum.Date:
      case FieldTypeEnum.Time: {
        return (
          <DatePicker
            showTime={true}
            disabled={true}
            placeholder="请选择日期"
          />
        )
      }
      case FieldTypeEnum.DateRange: {
        return (
          <DatePickerRange
            showTime={true}
            disabled={true}
          />
        )
      }
      case FieldTypeEnum.Attachment: {
        return (
          <div>
            <Upload disabled={true}>
              <UploadContent>
                <GrowpIcon
                  name="Plus"
                  color={theme.text._4}
                  size={14}
                />
                <div>上传附件</div>
              </UploadContent>
            </Upload>
            {questionnaire.metadata?.fileSizeLimit ? (
              <div
                style={{
                  fontSize: '12px',
                  color: theme.text._3,
                  marginTop: '8px',
                }}
              >
                单个文件大小不超过 {questionnaire.metadata?.fileSizeLimit} MB
              </div>
            ) : null}
          </div>
        )
      }
      case FieldTypeEnum.Address: {
        return (
          <Cascader
            disabled={true}
            placeholder="请选择"
          />
        )
      }
      case FieldTypeEnum.Describe: {
        return (
          <Describe>
            <Input.TextArea
              autoSize={{ minRows: 1 }}
              maxLength={10000}
              placeholder="请输入文本描述，支持「Enter」换行"
              value={questionnaire.label}
              onChange={(e) => onChangeData({ label: e.target.value })}
              style={{ fontSize: 18 }}
            />
          </Describe>
        )
      }
      case FieldTypeEnum.PageBreak: {
        return <Divider dashed={true}>分页</Divider>
      }
      case FieldTypeEnum.Single: {
        if (isProductChild) {
          return (
            <OptionList
              options={(questionnaire.options as Option[]) || []}
              type={FieldTypeEnum.Single}
              selectType={
                questionnaire.metadata?.selectType || SelectTypeEnum.Checked
              }
            />
          )
        }
        return (
          <OptionSetting
            type={FieldTypeEnum.Single}
            options={(questionnaire.options as Option[]) || []}
            currentIndex={index}
          />
        )
      }
      case FieldTypeEnum.Multi: {
        if (isProductChild) {
          return (
            <OptionList
              options={(questionnaire.options as Option[]) || []}
              type={FieldTypeEnum.Multi}
              selectType={
                questionnaire.metadata?.selectType || SelectTypeEnum.Checked
              }
            />
          )
        }
        return (
          <OptionSetting
            type={FieldTypeEnum.Multi}
            options={(questionnaire.options as Option[]) || []}
            currentIndex={index}
          />
        )
      }
      case FieldTypeEnum.Product: {
        return (
          <ProductWrapper>
            {isArray(questionnaire.children)
              ? questionnaire.children?.map(
                  (child: QuestionnaireReqColumn, index: number) => {
                    return (
                      <ProductChildWrapper key={child.id || index}>
                        <ChildTitleWrapper>
                          <RequireStar $require={!!child.require}>
                            {child.require ? '*' : ''}
                          </RequireStar>
                          {child.label}
                        </ChildTitleWrapper>
                        {getComponent(
                          child as ReqColumnWithMetaData,
                          index,
                          true
                        )}
                      </ProductChildWrapper>
                    )
                  }
                )
              : null}
          </ProductWrapper>
        )
      }
    }
  }
  return <Wrapper>{getComponent(questionnaire, currentIndex)}</Wrapper>
}

const Wrapper = styled.div`
  padding: 12px 0;
  .ant-picker-disabled {
    background-color: ${(props) => props.theme.background._3} !important;
  }
  .ant-input,
  .ant-input-number,
  .ant-picker,
  .ant-select {
    width: 680px;
    height: 40px;
    border: none;
    border-radius: 8px;
    padding: 9px 16px;
    background-color: ${(props) => props.theme.background._3};
  }
  .ant-input-number-input {
    padding-left: 0px;
  }
  .ant-select {
    display: flex;
    justify-content: center;
    align-items: center;
    .ant-select-selector {
      border: none;
      padding-left: 0;
      background-color: transparent !important;
    }
  }
  .ant-divider {
    font-size: 14px;
    font-weight: 400;
    color: ${(props) => props.theme.text._1};
  }
`

const UploadContent = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  padding-top: 16px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border: 1px dashed ${(props) => props.theme.border.greyNormal};
  background-color: ${(props) => props.theme.background._3};
  & > div {
    font-size: 14px;
    color: ${(props) => props.theme.text._4};
  }
`

const Describe = styled.div`
  width: 100%;
  & .ant-input {
    background-color: transparent;
    padding-left: 0;
    padding-right: 0;
    border-radius: 0;
    border-bottom: 1px solid transparent;
  }
  .ant-input:focus,
  .ant-input:hover {
    border-bottom: 1px solid ${(props) => props.theme.brand._1} !important;
  }
`

const ProductWrapper = styled.div``

const ProductChildWrapper = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`

const ChildTitleWrapper = styled.div`
  width: 100%;
  height: 22px;
  margin-bottom: 8px;
  display: flex;
  justify-self: flex-start;
  align-items: center;
  font-size: 14px;
  color: ${(props) => props.theme.text._2};
`

const RequireStar = styled.div<{ $require: boolean }>`
  width: ${(props) => (props.$require ? '17px' : 0)};
  text-align: center;
  font-size: 16px;
  padding-top: 3px;
  color: #f54545;
`
